import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAvatar from "components/MDAvatar";
import { getAllCompaniesForSuperUser } from "services/api";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";
import { useNavigate } from "react-router-dom";

// API Import - CHANGE THIS PATH to where your api file is located

function SuperAdminPanel() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch Data Function
  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getAllCompaniesForSuperUser();
      setCompanies(response.data || response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("Failed to load companies. Ensure backend is running.");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderLogo = (logoBytes) => {
    if (logoBytes) return `data:image/jpeg;base64,${logoBytes}`;
    return null; // Fallback handled in UI
  };

  return (
    <MDBox py={3}>
      {/* ================= COMPANY GRID ================= */}
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={3}
          px={2}
          variant="gradient"
          bgColor="primary"
          borderRadius="lg"
          coloredShadow="primary"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox>
            <MDTypography variant="h6" color="white">
              Registered Companies
            </MDTypography>
            <MDTypography variant="button" color="white" opacity={0.8}>
              Manage databases, logs, and subscriptions
            </MDTypography>
          </MDBox>
          <MDButton
            variant="outlined"
            color="white"
            size="small"
            onClick={fetchData}
            startIcon={<Icon>refresh</Icon>}
          >
            Refresh
          </MDButton>
        </MDBox>

        <MDBox p={3}>
          {loading ? (
            <MDBox display="flex" flexDirection="column" alignItems="center" py={10}>
              <CircularProgress color="info" size={50} />
              <MDTypography variant="button" color="text" mt={2}>
                Loading Tenant Data...
              </MDTypography>
            </MDBox>
          ) : error ? (
            <MDBox textAlign="center" py={5}>
              <Icon fontSize="large" color="error">
                error_outline
              </Icon>
              <MDTypography color="error" mt={1}>
                {error}
              </MDTypography>
              <MDButton variant="text" color="info" onClick={fetchData} sx={{ mt: 2 }}>
                Try Again
              </MDButton>
            </MDBox>
          ) : (
            <Grid container spacing={3}>
              {companies?.map((company) => (
                <Grid item xs={12} md={6} lg={4} xl={3} key={company.companyId}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 12px 20px -10px rgba(0,172,193, 0.28)",
                      },
                    }}
                  >
                    {/* --- CONTENT --- */}
                    <MDBox p={2}>
                      {/* LOGO */}
                      <MDBox
                        sx={(theme) => ({
                          height: "100px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "12px",
                          backgroundColor: darkMode
                            ? theme.functions.rgba("#FFF", 0.05)
                            : theme.functions.rgba("#000", 0.04),
                          border: `1px solid ${theme.functions.rgba("#000", 0.05)}`,
                          mb: 2,
                          overflow: "hidden",
                          padding: "10px",
                        })}
                      >
                        {company.companyLogo ? (
                          <MDBox
                            component="img"
                            src={renderLogo(company.companyLogo)}
                            alt={company.companyName}
                            sx={{
                              maxWidth: "80%",
                              maxHeight: "80%",
                              objectFit: "contain",
                              filter: darkMode ? "brightness(0) invert(1)" : "none",
                            }}
                          />
                        ) : (
                          <Icon fontSize="large" sx={{ opacity: 0.3, fontSize: "40px !important" }}>
                            business
                          </Icon>
                        )}
                      </MDBox>

                      {/* COMPANY NAME */}
                      <MDBox textAlign="center">
                        <MDTypography variant="h5" fontWeight="bold">
                          {company.companyName}
                        </MDTypography>

                        {/* LANGUAGE */}
                        <MDTypography variant="caption" color="text">
                          Language: {company.language || "N/A"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>

                    {/* --- ACTIONS --- */}
                    <MDBox p={2} pt={0}>
                      <MDButton
                        variant="gradient"
                        color="primary"
                        fullWidth
                        sx={{ mb: 1 }}
                        onClick={() => navigate("/super-admin/manage", { state: { company } })}
                      >
                        Manage Company
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="primary"
                        fullWidth
                        size="small"
                        onClick={() => navigate("/super-admin/logs", { state: { company } })}
                      >
                        View Audit Logs
                      </MDButton>
                    </MDBox>
                  </Card>
                </Grid>
              ))}

              {companies.length === 0 && (
                <Grid item xs={12}>
                  <MDBox textAlign="center" py={10} opacity={0.6}>
                    <Icon fontSize="large" sx={{ fontSize: "60px !important", mb: 2 }}>
                      folder_off
                    </Icon>
                    <MDTypography variant="h5">No companies found</MDTypography>
                    <MDTypography variant="body2">Check your database connections</MDTypography>
                  </MDBox>
                </Grid>
              )}
            </Grid>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default SuperAdminPanel;
